/**
 * ComfortMessage — Subtle ephemeral message shown after a few minutes of listening
 * 
 * "Avec le temps, le confort d'écoute s'installe"
 * 
 * Rules:
 * - Appears between 180s and 240s of playback
 * - Only if audio is playing
 * - Max 1 per session
 * - Only 1 session out of 3
 * - 24h anti-repeat
 * - Fade-in 400ms, visible 5s, fade-out 400ms
 * - No CTA, no icon, no blocking
 */
import React, { useState, useEffect, useRef } from 'react';

const LS_KEY_COUNT = 'hearen_comfort_msg_session_count';
const LS_KEY_LAST = 'hearen_comfort_msg_last_shown';

const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch {} };

const ComfortMessage = ({ elapsedSeconds, isPlaying }) => {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState('idle'); // idle | in | show | out
  const shownRef = useRef(false);
  const countedRef = useRef(false);

  useEffect(() => {
    // Count this session once
    if (!countedRef.current) {
      countedRef.current = true;
      const count = parseInt(safeGet(LS_KEY_COUNT) || '0', 10);
      safeSet(LS_KEY_COUNT, (count + 1).toString());
    }
  }, []);

  useEffect(() => {
    if (shownRef.current) return;
    if (!isPlaying) return;
    if (elapsedSeconds < 180 || elapsedSeconds > 240) return;

    // Check 1-out-of-3 sessions
    const count = parseInt(safeGet(LS_KEY_COUNT) || '1', 10);
    if (count % 3 !== 0) return;

    // Check 24h anti-repeat
    const lastShown = parseInt(safeGet(LS_KEY_LAST) || '0', 10);
    if (Date.now() - lastShown < 24 * 60 * 60 * 1000) return;

    // Show the message
    shownRef.current = true;
    safeSet(LS_KEY_LAST, Date.now().toString());

    setVisible(true);
    setFading('in');

    // After fade-in, hold
    const t1 = setTimeout(() => setFading('show'), 400);
    // After hold, fade out
    const t2 = setTimeout(() => setFading('out'), 5400);
    // After fade-out, hide
    const t3 = setTimeout(() => { setFading('idle'); setVisible(false); }, 5800);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [elapsedSeconds, isPlaying]);

  if (!visible) return null;

  const opacity = fading === 'in' ? 0 : fading === 'out' ? 0 : 0.65;

  return (
    <div
      className="absolute left-0 right-0 flex justify-center pointer-events-none z-10"
      style={{ bottom: 140 }}
      data-testid="comfort-message"
    >
      <p
        className="text-white text-xs text-center px-6 leading-relaxed"
        style={{
          opacity,
          transition: 'opacity 400ms ease',
          fontWeight: 400,
          letterSpacing: '0.01em',
        }}
      >
        Avec le temps, le confort d'écoute s'installe
      </p>
    </div>
  );
};

export default ComfortMessage;
